/**
 * Time-saved calculation logic for PureBox.
 *
 * Estimates how much manual triage time the user avoids by
 * applying PureBox classifications.  Every email type carries a
 * per-item weight (in seconds) derived from the product plan.
 *
 * @module time-saved
 */

// ---------------------------------------------------------------------------
// Types & constants
// ---------------------------------------------------------------------------

export type EmailTimeType =
  | 'newsletter'
  | 'promotional'
  | 'transactional'
  | 'notification'
  | 'actionable'
  | 'unknown'

/**
 * Seconds a user would spend manually triaging one email of each type.
 *
 * Source: CORE_PRODUCT_GUIDELINES.md § Time-Saved Calculation
 *   Newsletter / promo   → 10 s
 *   Transactional         → 30 s
 *   Notification          → 15 s
 *   Actionable            → 60 s
 *   Unknown / ambiguous   → 45 s
 */
export const TIME_WEIGHTS: Record<EmailTimeType, number> = {
  newsletter: 15,
  promotional: 15,
  transactional: 45,
  notification: 15,
  actionable: 60,
  unknown: 45,
}

// ---------------------------------------------------------------------------
// Core calculation
// ---------------------------------------------------------------------------

/**
 * Calculate total estimated seconds saved.
 *
 * @param emailCounts  A partial map of email type → count of emails processed
 * @returns            Total seconds saved (always ≥ 0)
 *
 * @example
 * ```ts
 * calculateTimeSaved({ newsletter: 50, transactional: 20 })
 * // => 50*10 + 20*30 = 1100
 * ```
 */
export function calculateTimeSaved(
  emailCounts: Partial<Record<EmailTimeType, number>>,
): number {
  let total = 0
  for (const [type, count] of Object.entries(emailCounts)) {
    const weight = TIME_WEIGHTS[type as EmailTimeType]
    if (weight !== undefined && typeof count === 'number' && count > 0) {
      total += weight * count
    }
  }
  return total
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

const SECONDS_PER_MINUTE = 60
const SECONDS_PER_HOUR = 3_600
const SECONDS_PER_DAY = 86_400

/**
 * Human-readable long format.
 *
 * Rules (from CORE_PRODUCT_GUIDELINES.md):
 *   - Round to nearest meaningful unit
 *   - Prefix with `~`
 *   - Never label as "saved" - the caller decides the verb
 *
 * @returns e.g. `"~12 minutes"`, `"~2 hours"`, `"~3 days"`
 */
export function formatTimeSaved(seconds: number): string {
  if (seconds <= 0) return '~0 seconds'

  if (seconds < SECONDS_PER_MINUTE) {
    const s = Math.round(seconds)
    return `~${s} second${s === 1 ? '' : 's'}`
  }

  if (seconds < SECONDS_PER_HOUR) {
    const m = Math.round(seconds / SECONDS_PER_MINUTE)
    return `~${m} minute${m === 1 ? '' : 's'}`
  }

  if (seconds < SECONDS_PER_DAY) {
    const h = Math.round(seconds / SECONDS_PER_HOUR)
    return `~${h} hour${h === 1 ? '' : 's'}`
  }

  const d = Math.round(seconds / SECONDS_PER_DAY)
  return `~${d} day${d === 1 ? '' : 's'}`
}

/**
 * Short badge format.
 *
 * @returns e.g. `"12 min"`, `"2 hrs"`, `"3 days"`
 */
export function formatTimeSavedShort(seconds: number): string {
  if (seconds <= 0) return '0 sec'

  if (seconds < SECONDS_PER_MINUTE) {
    const s = Math.round(seconds)
    return `${s} sec`
  }

  if (seconds < SECONDS_PER_HOUR) {
    const m = Math.round(seconds / SECONDS_PER_MINUTE)
    return `${m} min`
  }

  if (seconds < SECONDS_PER_DAY) {
    const h = Math.round(seconds / SECONDS_PER_HOUR)
    return `${h} ${h === 1 ? 'hr' : 'hrs'}`
  }

  const d = Math.round(seconds / SECONDS_PER_DAY)
  return `${d} ${d === 1 ? 'day' : 'days'}`
}
